import { forwardRef, useId, type TextareaHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './TextArea.module.css';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
}

/** Multi-line text input. Use for notes / descriptions. */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    helperText,
    errorText,
    required,
    fullWidth,
    id,
    className,
    rows = 3,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `textarea-${reactId}`;
  const helperId = helperText || errorText ? `${inputId}-helper` : undefined;
  const hasError = Boolean(errorText);

  return (
    <div className={clsx(styles.field, fullWidth && styles.fullWidth, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        aria-invalid={hasError || undefined}
        aria-describedby={helperId}
        required={required}
        className={clsx(styles.textarea, hasError && styles.hasError)}
        {...rest}
      />
      {(errorText || helperText) && (
        <p id={helperId} className={clsx(styles.helper, hasError && styles.errorText)}>
          {errorText ?? helperText}
        </p>
      )}
    </div>
  );
});
